import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import _isEqual from 'lodash/isEqual';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';

import { Chip, Link } from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { DigitalRetailOverlayCTA } from 'reaxl-wallet';

import offsetCalc from '@/utilities/offSetScrollCalculation';
import scrollToElement from '@/utilities/scrollToElement';

import { inventoryProductClick } from '@/analytics/inventoryAnalyticsHandlers';
import { shoppingAssistantCTAClick } from '@/analytics/shoppingAssistantEvents';

import dealsDuck from '@/ducks/dealsDuck.js';
import listingsAgentDuck from '@/ducks/listingsAgentDuck';
import paymentsDuck from '@/ducks/paymentsDuck';
import userDuck from '@/ducks/userDuck';
import vdpActiveEmailListingDuck from '@/ducks/vdp/vdpActiveEmailListingDuck.js';
import vdpActiveInteractionDuck from '@/ducks/vdp/vdpActiveInteractionDuck.js';
import vdpModelInfoDuck from '@/ducks/vdp/vdpModelInfoDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import useShoppingAssistantOwnerChat from '@/hooks/useShoppingAssistantOwnerChat';

import ShoppingAssistantContainer from '@/containers/ShoppingAssistantContainer';

/**
 * React component that renders a shopping assistant with suggested actions for VDP pages.
 *
 * Provides various CTAs (Call-to-Actions) like check availability, schedule test drive,
 * vehicle history report, estimate payment, and features/specs. Includes guard logic
 * to prevent unnecessary API calls when styleId is undefined and handles chat functionality.
 *
 * @function VdpShoppingAssistantContainer
 */
function VdpShoppingAssistantContainer() {
    const {
        shopping_assistant: [, { listings_agent: enableListingsAgent }],
    } = useFeatures([
        'shopping_assistant',
    ]);

    const [showAssistant, setShowAssistant] = useState(false);

    const dispatch = useDispatch();

    const { isBrand } = useBrand();
    const isKbb = isBrand('kbb');

    const paymentsInfo = useSelector(paymentsDuck.selectors.getPaymentsInfo);
    const modelInfo = useSelector(vdpModelInfoDuck.selectors.getModelInfo);
    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const listingHasVehicleSummary = useSelector((state) => listingsAgentDuck.selectors.hasVehicleSummary(state, listing?.id));

    const {
        styleId,
        vhrTiles = [],
    } = listing || {};

    useEffect(() => {
        if (styleId) {
            dispatch(vdpModelInfoDuck.creators.loadModelInfo({ styleId }));
        }
        return () => { dispatch(vdpModelInfoDuck.creators.clear()); };
    }, [styleId, dispatch]);

    const handleAssistantClose = () => {
        setShowAssistant(false);
    };

    const handleSellerChatClick = () => {
        handleAssistantClose();
    };

    const { chatWithSellerCta, chatWithSellerCtaContainer } = useShoppingAssistantOwnerChat(handleSellerChatClick);

    const checkAvailabilityTitle = 'Check availability';
    const scheduleTestDriveTitle = 'Schedule test drive';
    const vehicleHistoryReportTitle = 'See history report';
    const estimatePaymentTitle = 'Estimate payment';
    const vehicleSpecificationsTitle = 'See features and specs';
    const vehicleSummaryTitle = 'What\'s great about this vehicle?';

    const handleSendClickShoppingAssistantCTA = (event, payload = {}) => {
        const { input, tagIndex } = payload;

        sendClick(shoppingAssistantCTAClick, event, {
            input: input || event.rawText,
            isKbb,
            tagIndex,
        });
    };

    const handleCheckAvailabilityClick = (event) => {
        handleAssistantClose();
        setTimeout(() => {
            dispatch(vdpActiveInteractionDuck.creators.setKeys({
                showEmailModal: true,
            }));
        }, 300);
        handleSendClickShoppingAssistantCTA(event, { input: checkAvailabilityTitle, tagIndex: 'vdp-165' });
    };

    const checkAvailabilityCta = (
        <Chip
            uiContext="inquiry"
            onClick={handleCheckAvailabilityClick}
            data-cmp="suggested-filter-button"
            label={checkAvailabilityTitle}
        />
    );

    const handleTestDriveCtaClick = (event) => {
        handleAssistantClose();

        handleSendClickShoppingAssistantCTA(event, { input: scheduleTestDriveTitle, tagIndex: 'vdp-163' });

        if (!listing?.accelerate) {
            setTimeout(() => {
                dispatch(vdpActiveEmailListingDuck.creators.setActiveResults([listing.id]));
                dispatch(vdpActiveInteractionDuck.creators.setKeys({
                    emailCTAContext: 'testDrive',
                    showEmailModal: true,
                }));
            }, 300);
        }
    };

    const scheduleTestDriveCta = listing.accelerate ? (
        <DigitalRetailOverlayCTA
            data-cmp="suggested-filter-button"
            dealsDuck={dealsDuck}
            listing={listing}
            onClick={handleTestDriveCtaClick}
            paymentsDuck={paymentsDuck}
            paymentsInfo={paymentsInfo}
            uiContext="shoppingAssistant"
            userDuck={userDuck}
        />
    ) : (
        <Chip
            uiContext="inquiry"
            onClick={handleTestDriveCtaClick}
            data-cmp="suggested-filter-button"
            label={scheduleTestDriveTitle}
        />
    );

    const handleVehicleHistoryReportClick = (event) => {
        const { epn, tileType } = vhrTiles?.[0] || {};
        handleAssistantClose();
        sendClick(inventoryProductClick, event, {
            epn,
            isShoppingAssistantCtaClick: true,
            tileType,
        });
    };

    const vhrLink = vhrTiles?.[0]?.link?.href;
    const vehicleHistoryReportCta = !!vhrLink && (
        <Link
            href={vhrLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleVehicleHistoryReportClick}
        >
            <Chip
                uiContext="inquiry"
                data-cmp="suggested-filter-button"
                label={vehicleHistoryReportTitle}
            />
        </Link>
    );

    const scrollToNativeDealSection = () => {
        const offset = offsetCalc(25, 'sticky-header-vdp');
        const isNDSRendered = document.getElementById('nds');
        const destination = isNDSRendered ? 'nds' : 'nativeDealContainer';
        scrollToElement(destination, offset);
    };

    const handleWalletPricingDetailsClick = (event) => {
        handleAssistantClose();
        setTimeout(() => {
            scrollToNativeDealSection();
        }, 300);
        handleSendClickShoppingAssistantCTA(event, { input: estimatePaymentTitle, tagIndex: 'vdp-164' });
    };

    const estimatePaymentCta = (
        <Chip
            uiContext="inquiry"
            onClick={handleWalletPricingDetailsClick}
            data-cmp="suggested-filter-button"
            label={estimatePaymentTitle}
        />
    );

    const handleFeaturesSpecsClick = (event) => {
        handleAssistantClose();
        setTimeout(() => {
            dispatch(vdpActiveInteractionDuck.creators.setKeys({
                showModelInfoModal: true,
            }));
        }, 300);
        handleSendClickShoppingAssistantCTA(event, { input: vehicleSpecificationsTitle, tagIndex: 'vdp-166' });
    };

    const requestVehicleSummary = async () => {
        dispatch(listingsAgentDuck.creators.sendVehicleSummaryMessage());
    };

    const handleVehicleSummaryClick = (event) => {
        handleSendClickShoppingAssistantCTA(event, {
            input: vehicleSummaryTitle || event.rawText,
            isKbb,
            tagIndex: 'vdp-154',
        });

        requestVehicleSummary();
    };

    const vehicleSpecificationsCta = (modelInfo && Object.keys(modelInfo).length > 0) && (
        <Chip
            uiContext="inquiry"
            onClick={handleFeaturesSpecsClick}
            data-cmp="suggested-filter-button"
            label={vehicleSpecificationsTitle}
        />
    );

    const vehicleSummaryCta = enableListingsAgent && (
        <Chip
            uiContext="inquiry"
            onClick={handleVehicleSummaryClick}
            data-cmp="suggested-filter-button"
            label={vehicleSummaryTitle}
        />
    );

    const suggestedActions = (
        <>
            {!listingHasVehicleSummary && vehicleSummaryCta}
            {checkAvailabilityCta}
            {scheduleTestDriveCta}
            {chatWithSellerCtaContainer}
            {vehicleHistoryReportCta}
            {estimatePaymentCta}
            {vehicleSpecificationsCta}
        </>
    );

    return (
        <>
            {chatWithSellerCta}
            <ShoppingAssistantContainer
                handleAssistantClose={handleAssistantClose}
                setShowAssistant={setShowAssistant}
                showAssistant={showAssistant}
                suggestedActions={suggestedActions}
            />
        </>
    );
}

export default React.memo(VdpShoppingAssistantContainer, _isEqual);
